import moment, { Moment } from 'moment-timezone'
import React from 'react'
import VirtualCardRevisionsFrequency from 'src/components/VirtualCardRevisionsFrequency/VirtualCardRevisionsFrequency'
import { ExtendCurrency } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import { translateNode } from 'src/localization/translateNode'
import {
  RecurrenceModel,
  RecurrenceResponse,
} from 'src/models/recurrence.models'
import { UserListModel, UserListResponse } from 'src/models/user.models'
import {
  ApprovalStatus,
  IssuerCycleConfig,
  VirtualCardBillPayMetaModel,
  VirtualCardBillPayMetaResponse,
  VirtualCardPendingRevisionModel,
  VirtualCardPendingRevisionResponse,
  VirtualCardStatus,
  VirtualCardType,
} from 'src/models/virtualCard.models'
import { groupArrayBy } from 'src/utils/array.utils'
import { toDollars } from 'src/utils/currency.utils'

enum VirtualCardRevisionEvent {
  Approved = 'APPROVED',
  Cancelled = 'CANCELLED',
  Expired = 'EXPIRED',
  FailedSend = 'FAILED_SEND',
  ModApproved = 'MOD_APPROVED',
  ModCancelled = 'MOD_CANCELLED',
  ModRejected = 'MOD_REJECTED',
  ModRequested = 'MOD_REQUESTED',
  IssuerCycled = 'ISSUER_CYCLED',
  Recurred = 'RECURRED',
  Rejected = 'REJECTED',
  Requested = 'REQUESTED',
  Restored = 'RESTORED',
  Sent = 'SENT',
  Updated = 'UPDATED',
  BalanceUpdated = 'BALANCE_UPDATED',
  Closed = 'CLOSED',
}

export interface GetVirtualCardRevisionsQueryParams {
  limit?: number
  previousCursor?: string
  nextCursor?: string
  order?: string
}

export interface GetVirtualCardRevisionsPagination {
  previousCursor?: string
  nextCursor?: string
}

export interface VirtualCardRevisionListResponse {
  id: string
  event: VirtualCardRevisionEvent
  timestamp: string
  operator?: UserListResponse
  before?: RevisionDiffResponse
  after?: RevisionDiffResponse
}

export interface FieldChangeGroup {
  label: React.ReactNode | string
  changes: FieldChange[]
}

export interface FieldChange {
  message?: React.ReactElement | string
  label?: React.ReactNode | string
  value?: React.ReactElement | string
}

export class VirtualCardRevisionListModel {
  public id: string
  public event: VirtualCardRevisionEvent
  public timestamp: string
  public operator?: UserListModel
  public before?: RevisionDiffModel
  public after?: RevisionDiffModel
  public cardType?: VirtualCardType
  public vendorName?: string

  public constructor(
    data: VirtualCardRevisionListResponse,
    cardType?: VirtualCardType,
    vendorName?: string,
  ) {
    this.id = data.id
    this.event = data.event
    this.timestamp = data.timestamp
    this.operator =
      data.operator !== undefined ? new UserListModel(data.operator) : undefined
    this.before =
      data.before !== undefined ? new RevisionDiffModel(data.before) : undefined
    this.after =
      data.after !== undefined ? new RevisionDiffModel(data.after) : undefined
    this.cardType = cardType
    this.vendorName = vendorName
  }

  public get dateLabel(): string {
    return moment.tz(this.timestamp, moment.tz.guess()).format('l')
  }

  // TODO: feels bad to explicitly use guess here just to get a timezone label
  public get timeLabel(): React.ReactElement {
    const mom = moment.tz(this.timestamp, moment.tz.guess())
    return translateNode('${time}, ${timezone}', {
      time: mom.format('LT'),
      timezone: mom.format('z'),
    })
  }

  public get dateTimeLabel(): React.ReactElement {
    return translateNode('${date} at ${timeLabel}', {
      date: this.dateLabel,
      timeLabel: this.timeLabel,
    })
  }

  public timezonedDateLabel(timezone: string): string {
    return moment.tz(this.timestamp, timezone).format('l')
  }

  public timezonedTimeLabel(timezone: string): React.ReactElement {
    const mom = moment.tz(this.timestamp, timezone)
    return translateNode('${time}, ${timezone}', {
      time: mom.format('LT'),
      timezone: mom.zoneAbbr(),
    })
  }

  public timezonedDateTimeLabel(timezone: string): React.ReactElement {
    return translateNode('${date} at ${timeLabel}', {
      date: this.timezonedDateLabel(timezone),
      timeLabel: this.timezonedTimeLabel(timezone),
    })
  }

  public eventSubject(userId: string): React.ReactElement | string {
    if (this.operator !== undefined && this.operator.id === userId) {
      return translateNode('You')
    } else if (this.operator !== undefined) {
      return this.operator.truncatedFullNameOrEmail
    } else if (
      this.event === VirtualCardRevisionEvent.Sent &&
      this.after?.cardholder !== undefined
    ) {
      return this.after.cardholder.truncatedFullNameOrEmail
    } else if (
      this.event === VirtualCardRevisionEvent.Requested &&
      this.after?.recipient !== undefined
    ) {
      return this.after.recipient.truncatedFullNameOrEmail
    } else if (
      this.event === VirtualCardRevisionEvent.Recurred ||
      this.event === VirtualCardRevisionEvent.IssuerCycled ||
      this.event === VirtualCardRevisionEvent.Expired
    ) {
      return translateNode(`This ${this.cardTypeSource}`)
    } else {
      return translateNode('The system')
    }
  }

  public eventObject(userId: string): React.ReactElement | string {
    if (this.event === VirtualCardRevisionEvent.Sent) {
      if (
        this.cardType === VirtualCardType.BillPay &&
        this.after?.vendorEmail
      ) {
        return translateNode('vendor ${vendorName}', {
          vendorName: this.vendorName ?? this.after.vendorEmail,
        })
      } else if (userId === this.after?.recipient?.id) {
        return this.operator !== undefined && this.operator.id === userId
          ? translateNode('yourself')
          : translateNode('you')
      } else if (this.after?.recipient !== undefined) {
        return this.after.recipient.truncatedFullNameOrEmail
      } else {
        return translateNode('Unknown')
      }
    } else if (this.event === VirtualCardRevisionEvent.Requested) {
      if (userId === this.after?.cardholder?.id) {
        return translateNode('you')
      } else if (this.after?.cardholder !== undefined) {
        return this.after.cardholder.truncatedFullNameOrEmail
      } else {
        return translateNode('Unknown')
      }
    } else {
      return translateNode('Unknown')
    }
  }

  public displayNameChanges(
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
    pending: boolean,
  ): FieldChange | undefined {
    // for bill pay Vendor Name drives display name
    if (this.cardType === VirtualCardType.BillPay) return undefined

    if (before?.displayName !== undefined && after?.displayName === undefined) {
      return {
        message: pending
          ? translateNode('Card Name removed from pending modification')
          : translateNode('Card Name removed'),
      }
    } else if (after?.displayName !== undefined) {
      return {
        label: translateNode('Card Name: '),
        value: after?.displayName,
      }
    } else {
      return undefined
    }
  }

  public vendorNameChanges(
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
    pending: boolean,
  ): FieldChange | undefined {
    if (before?.vendorName !== undefined && after?.vendorName === undefined) {
      return {
        message: pending
          ? translateNode('Vendor Name removed from pending modification')
          : translateNode('Vendor Name removed'),
      }
    } else if (after?.vendorName !== undefined) {
      return {
        label: translateNode('Vendor Name: '),
        value: after.vendorName,
      }
    } else {
      return undefined
    }
  }

  public vendorEmailChanges(
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
    pending: boolean,
  ): FieldChange | undefined {
    if (before?.vendorEmail !== undefined && after?.vendorEmail === undefined) {
      return {
        message: pending
          ? translateNode('Vendor Email removed from pending modification')
          : translateNode('Vendor Email removed'),
      }
    } else if (after?.vendorEmail !== undefined) {
      return {
        label: translateNode('Vendor Email: '),
        value: after.vendorEmail,
      }
    } else {
      return undefined
    }
  }

  public invoiceNumberChanges(
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
    pending: boolean,
  ): FieldChange | undefined {
    if (
      before?.invoiceNumber !== undefined &&
      after?.invoiceNumber === undefined
    ) {
      return {
        message: pending
          ? translateNode('Invoice Number removed from pending modification')
          : translateNode('Invoice Number removed'),
      }
    } else if (after?.invoiceNumber !== undefined) {
      return {
        label: translateNode('Invoice Number: '),
        value: after.invoiceNumber,
      }
    } else {
      return undefined
    }
  }

  public invoiceDateChanges(
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
    pending: boolean,
  ): FieldChange | undefined {
    if (
      before?.invoiceDateString !== undefined &&
      after?.invoiceDateString === undefined
    ) {
      return {
        message: pending
          ? translateNode('Invoice Date removed from pending modification')
          : translateNode('Invoice Date removed'),
      }
    } else if (after?.invoiceDateString !== undefined) {
      return {
        label: translateNode('Invoice Date: '),
        value: after.invoiceDateString,
      }
    } else {
      return undefined
    }
  }

  public balanceCentsChanges(
    before: RevisionDiffModel | VirtualCardPendingRevisionModel | undefined,
    after: RevisionDiffModel | VirtualCardPendingRevisionModel | undefined,
    pending: boolean,
    currency: ExtendCurrency,
  ): FieldChange | undefined {
    // Bill Pay cards cannot have their balance changed
    if (this.cardType === VirtualCardType.BillPay) return undefined

    const isApproved =
      this.event === VirtualCardRevisionEvent.Approved ||
      this.event === VirtualCardRevisionEvent.ModApproved
    if (
      before?.balanceCents !== undefined &&
      after?.balanceCents === undefined
    ) {
      return {
        message: pending
          ? translateNode('Available Credit removed from pending modification')
          : translateNode('Available Credit removed'),
      }
    } else if (
      (isApproved ||
        this.event === VirtualCardRevisionEvent.Sent ||
        this.event === VirtualCardRevisionEvent.Requested ||
        this.event === VirtualCardRevisionEvent.ModRequested ||
        this.event === VirtualCardRevisionEvent.Updated) &&
      after?.balance
    ) {
      return {
        label: isApproved
          ? translateNode('Available Credit: ')
          : translateNode('Credit Limit: '),
        value: (
          <Crncy
            amount={after.balance ?? after.balanceCents}
            currency={after.currency || currency}
          />
        ),
      }
    }
  }

  public limitCentsChanges(
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
    pending: boolean,
    currency: ExtendCurrency,
  ): FieldChange | undefined {
    // used to show initial limit for bill pay and issuer cycle cards
    if (
      this.cardType !== VirtualCardType.BillPay &&
      this.cardType !== VirtualCardType.IssuerCycle
    )
      return undefined

    const isApproved =
      this.event === VirtualCardRevisionEvent.Approved ||
      this.event === VirtualCardRevisionEvent.ModApproved
    if (before?.limitCents !== undefined && after?.limitCents === undefined) {
      return {
        message: pending
          ? translateNode(
              `${this.limitLabelSource} removed from pending modification`,
            )
          : translateNode(`${this.limitLabelSource} removed`),
      }
    } else if (
      (isApproved ||
        this.event === VirtualCardRevisionEvent.Sent ||
        this.event === VirtualCardRevisionEvent.Requested ||
        this.event === VirtualCardRevisionEvent.ModRequested ||
        this.event === VirtualCardRevisionEvent.Updated) &&
      after?.limit
    ) {
      return {
        label: isApproved
          ? // TODO: can we ever reach this path?
            translateNode('Available Credit: ')
          : translateNode(`${this.limitLabelSource}: `),
        value: (
          <Crncy amount={after.limit} currency={after.currency || currency} />
        ),
      }
    }
  }

  public validFromChanges(
    before: RevisionDiffModel | VirtualCardPendingRevisionModel | undefined,
    after: RevisionDiffModel | VirtualCardPendingRevisionModel | undefined,
    pending: boolean,
    timezone: string,
  ): FieldChange | undefined {
    if (
      this.event !== VirtualCardRevisionEvent.Sent &&
      this.event !== VirtualCardRevisionEvent.Requested &&
      after?.recurrence !== undefined
    ) {
      return undefined
    }

    const afterValidFromTimezonedString =
      after?.validFromTimezonedString(timezone)
    if (before?.validFrom !== undefined && after?.validFrom === undefined) {
      return {
        message: pending
          ? translateNode('Start Date removed from pending modification')
          : translateNode('Start Date removed'),
      }
    } else if (afterValidFromTimezonedString !== undefined) {
      return {
        label: translateNode('Start Date: '),
        value: afterValidFromTimezonedString,
      }
    }
  }

  public activeUntilChanges(
    before: RevisionDiffModel | VirtualCardPendingRevisionModel | undefined,
    after: RevisionDiffModel | VirtualCardPendingRevisionModel | undefined,
    pending: boolean,
    timezone: string,
  ): FieldChange | undefined {
    if (after?.recurrence !== undefined) {
      return undefined
    }

    const afterValidToTimezonedString = after?.validToTimezonedString(timezone)
    if (
      before?.activeUntilTimezonedString !== undefined &&
      after?.activeUntilTimezonedString === undefined
    ) {
      return {
        message: pending
          ? translateNode('Active Until removed from pending modification')
          : translateNode('Active Until removed'),
      }
    } else if (afterValidToTimezonedString !== undefined) {
      return {
        label: translateNode('Active Until: '),
        value: afterValidToTimezonedString,
      }
    } else {
      return undefined
    }
  }

  public recurrenceChanges(
    before: RevisionDiffModel | VirtualCardPendingRevisionModel | undefined,
    after: RevisionDiffModel | VirtualCardPendingRevisionModel | undefined,
    pending: boolean,
    timezone: string,
  ): FieldChange | undefined {
    if (pending && after?.recurs === false) {
      return {
        message: translateNode('Recurring removed'),
      }
    }
    if (before?.recurrence !== undefined && after?.recurrence === undefined) {
      return {
        message: pending
          ? translateNode('Recurring removed from pending modification')
          : translateNode('Recurring removed'),
      }
    } else if (after?.recurrence !== undefined) {
      return {
        label: translateNode('Recurring: '),
        value: (
          <VirtualCardRevisionsFrequency
            recurrence={after.recurrence}
            timezone={timezone}
            balance={toDollars(after.recurrence.balanceCents)}
            currency={after.currency}
          />
        ),
      }
    } else {
      return undefined
    }
  }

  public issuerCycleConfigChanges(
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
    timezone: string,
  ): FieldChange | undefined {
    if (this.cardType !== VirtualCardType.IssuerCycle) return undefined

    const beforeNextAt = before?.issuerCycleConfig?.nextAt
    const afterNextAt = after?.issuerCycleConfig?.nextAt
    if (beforeNextAt !== undefined && afterNextAt === undefined) {
      return {
        message: translateNode('Next Cycle Date removed'),
      }
    } else if (afterNextAt !== undefined) {
      const mom = moment.tz(afterNextAt, timezone)
      if (!mom.isValid()) return undefined
      return {
        label: translateNode('Next Cycle Date: '),
        value: mom.format('ll'),
      }
    }
  }

  public receiptAttachmentChanges(
    before: RevisionDiffModel | VirtualCardPendingRevisionModel | undefined,
    after: RevisionDiffModel | VirtualCardPendingRevisionModel | undefined,
    pending: boolean,
  ): FieldChange | undefined {
    if (
      (before?.receiptAttachments !== undefined &&
        after?.receiptAttachments === undefined) ||
      (before?.receiptAttachments !== undefined &&
        after?.receiptAttachments !== undefined &&
        after.receiptAttachments.length === 0)
    ) {
      return {
        message: pending
          ? translateNode(
              `${this.receiptLabelSource} removed from pending modification`,
            )
          : translateNode(`${this.receiptLabelSource} removed`),
      }
    } else if (
      after?.receiptAttachments !== undefined &&
      after.receiptAttachments.length > 0
    ) {
      return {
        message: translateNode(`${this.receiptLabelSource} updated`),
      }
    }
  }

  public cardholderChanges(
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
    pending: boolean,
  ): FieldChange | undefined {
    if (before?.cardholder !== undefined && after?.cardholder === undefined) {
      return {
        message: pending
          ? translateNode('Card Creator removed from pending modification')
          : translateNode('Card Creator removed'),
      }
    } else if (after?.cardholder !== undefined) {
      return {
        label: translateNode('Card Creator: '),
        value: after.cardholder.truncatedFullNameOrEmail,
      }
    }
  }

  public recipientChanges(
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
    pending: boolean,
  ): FieldChange | undefined {
    // we don't show recipient for bill pay, replaced by Vendor Email
    if (this.cardType === VirtualCardType.BillPay) return undefined

    if (before?.recipient !== undefined && after?.recipient === undefined) {
      return {
        message: pending
          ? translateNode('Card User removed from pending modification')
          : translateNode('Card User removed'),
      }
    } else if (after?.recipient !== undefined) {
      return {
        label: translateNode('Card User: '),
        value: after.recipient.truncatedFullNameOrEmail,
      }
    }
  }

  public statusChanges(
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
    pending: boolean,
  ): FieldChange | undefined {
    if (before?.status !== undefined && after?.status === undefined) {
      return {
        message: pending
          ? translateNode('Status removed from pending modification')
          : translateNode('Status removed'),
      }
    } else if (after?.status !== undefined && after.status.length > 0) {
      return {
        label: translateNode('Status: '),
        value: after.psuedoStatusLabel,
      }
    } else if (
      before?.awaitingApprovalStatus ===
        ApprovalStatus.AwaitingCreditCardUserApproval &&
      after?.awaitingApprovalStatus ===
        ApprovalStatus.AwaitingApproverRuleApproval
    ) {
      return {
        label: translateNode('Status: '),
        value: translateNode('Pending Final Approval'),
      }
    }
  }

  public notesChanges(
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
    pending: boolean,
  ): FieldChange | undefined {
    if (before?.notes !== undefined && after?.notes === undefined) {
      return {
        message: pending
          ? translateNode('Notes removed from pending modification')
          : translateNode('Notes removed'),
      }
    } else if (after?.notes !== undefined) {
      return {
        label: translateNode('Notes: '),
        value:
          after.notes.length > 200
            ? `${after.notes.slice(0, 200)}...`
            : after.notes,
      }
    }
  }

  public receiptRulesExemptChanges(
    before: RevisionDiffModel | undefined,
    after: RevisionDiffModel | undefined,
  ): FieldChange | undefined {
    if (before?.receiptRulesExempt !== after?.receiptRulesExempt) {
      if (after?.receiptRulesExempt) {
        return {
          label: translateNode('Receipt rules: '),
          value: translateNode('Off'),
        }
      } else {
        return {
          label: translateNode('Receipt rules: '),
          value: translateNode('On'),
        }
      }
    }
  }

  public activeChanges(
    timezone: string,
    currency: ExtendCurrency,
  ): FieldChange[] {
    const acs = []

    const bef = this.before
    const aft = this.after

    const displayNameChanges = this.displayNameChanges(bef, aft, false)
    const vendorNameChanges = this.vendorNameChanges(bef, aft, false)
    const invoiceNumberChanges = this.invoiceNumberChanges(bef, aft, false)
    const invoiceDateChanges = this.invoiceDateChanges(bef, aft, false)
    const balanceCentsChanges = this.balanceCentsChanges(
      bef,
      aft,
      false,
      currency,
    )
    const limitCentsChanges = this.limitCentsChanges(bef, aft, false, currency)
    const validFromChanges = this.validFromChanges(bef, aft, false, timezone)
    const activeUntilChanges = this.activeUntilChanges(
      bef,
      aft,
      false,
      timezone,
    )
    const recurrenceChanges = this.recurrenceChanges(bef, aft, false, timezone)
    const issuerCycleConfigChanges = this.issuerCycleConfigChanges(
      bef,
      aft,
      timezone,
    )
    const receiptAttachmentChanges = this.receiptAttachmentChanges(
      bef,
      aft,
      false,
    )
    const cardholderChanges = this.cardholderChanges(bef, aft, false)
    const recipientChanges = this.recipientChanges(bef, aft, false)
    const vendorEmailChanges = this.vendorEmailChanges(bef, aft, false)
    const statusChanges = this.statusChanges(bef, aft, false)
    const notesChanges = this.notesChanges(bef, aft, false)
    const receiptRulesExemptChanges = this.receiptRulesExemptChanges(bef, aft)

    if (statusChanges !== undefined) {
      acs.push(statusChanges)
    }
    if (cardholderChanges !== undefined) {
      acs.push(cardholderChanges)
    }
    if (recipientChanges !== undefined) {
      acs.push(recipientChanges)
    }
    if (vendorEmailChanges !== undefined) {
      acs.push(vendorEmailChanges)
    }
    if (displayNameChanges !== undefined) {
      acs.push(displayNameChanges)
    }
    if (vendorNameChanges !== undefined) {
      acs.push(vendorNameChanges)
    }
    if (invoiceNumberChanges !== undefined) {
      acs.push(invoiceNumberChanges)
    }
    if (invoiceDateChanges !== undefined) {
      acs.push(invoiceDateChanges)
    }
    if (balanceCentsChanges !== undefined) {
      acs.push(balanceCentsChanges)
    }
    if (limitCentsChanges !== undefined) {
      acs.push(limitCentsChanges)
    }
    if (validFromChanges !== undefined) {
      acs.push(validFromChanges)
    }
    if (activeUntilChanges !== undefined) {
      acs.push(activeUntilChanges)
    }
    if (recurrenceChanges !== undefined) {
      acs.push(recurrenceChanges)
    }
    if (issuerCycleConfigChanges !== undefined) {
      acs.push(issuerCycleConfigChanges)
    }
    if (receiptAttachmentChanges !== undefined) {
      acs.push(receiptAttachmentChanges)
    }
    if (notesChanges !== undefined) {
      acs.push(notesChanges)
    }
    if (receiptRulesExemptChanges !== undefined) {
      acs.push(receiptRulesExemptChanges)
    }

    return acs
  }

  public pendingChanges(
    timezone: string,
    currency: ExtendCurrency,
  ): FieldChange[] {
    const pcs = []

    const bef = this.before?.pending
    const aft = this.after?.pending

    const balanceCentsChanges = this.balanceCentsChanges(
      bef,
      aft,
      true,
      currency,
    )
    const validFromChanges = this.validFromChanges(bef, aft, true, timezone)
    const activeUntilChanges = this.activeUntilChanges(bef, aft, true, timezone)
    const recurrenceChanges = this.recurrenceChanges(bef, aft, true, timezone)
    const receiptAttachmentChanges = this.receiptAttachmentChanges(
      bef,
      aft,
      true,
    )

    if (balanceCentsChanges !== undefined) {
      pcs.push(balanceCentsChanges)
    }
    if (validFromChanges !== undefined) {
      pcs.push(validFromChanges)
    }
    if (activeUntilChanges !== undefined) {
      pcs.push(activeUntilChanges)
    }
    if (recurrenceChanges !== undefined) {
      pcs.push(recurrenceChanges)
    }
    if (receiptAttachmentChanges !== undefined) {
      pcs.push(receiptAttachmentChanges)
    }

    return pcs
  }

  public fieldChangeGroups(
    userId: string,
    timezone: string,
    currency: ExtendCurrency,
  ): FieldChangeGroup[] {
    const subject = this.eventSubject(userId)
    const object = this.eventObject(userId)
    const activeChanges = groupArrayBy(
      this.activeChanges(timezone, currency),
      'message',
      (a) => (!a ? -1 : 1),
    ).flat()
    const pendingChanges = groupArrayBy(
      this.pendingChanges(timezone, currency),
      'message',
      (a) => (!a ? -1 : 1),
    ).flat()

    switch (this.event) {
      case VirtualCardRevisionEvent.BalanceUpdated: {
        return this.after?.balanceCents !== undefined
          ? [
              {
                label: translateNode('Available Credit updated to ${amount}', {
                  amount: (
                    <Crncy
                      currency={currency}
                      amount={toDollars(this.after.balanceCents)}
                    />
                  ),
                }),
                changes: [],
              },
            ]
          : []
      }
      case VirtualCardRevisionEvent.FailedSend: {
        return [
          {
            label: translateNode(
              `This ${this.cardTypeSource} could not be delivered to the recipient. Please check the email for typos, and send a new card.`,
            ),
            changes: [],
          },
        ]
      }
      case VirtualCardRevisionEvent.Sent: {
        return [
          {
            label: translateNode(
              '${subject} sent this ${cardType} to ${object}',
              {
                subject: subject,
                cardType: translateNode(this.cardTypeSource),
                object: object,
              },
            ),
            changes: activeChanges,
          },
        ]
      }
      case VirtualCardRevisionEvent.Requested: {
        if (
          this.after?.awaitingApprovalStatus ===
          ApprovalStatus.AwaitingApproverRuleApproval
        ) {
          return [
            {
              label: translateNode('${subject} requested this ${cardType}', {
                subject: subject,
                cardType: translateNode(this.cardTypeSource),
              }),
              changes: activeChanges,
            },
          ]
        }
        return [
          {
            label: translateNode(
              '${subject} requested this ${cardType} from ${object}',
              {
                subject: subject,
                cardType: translateNode(this.cardTypeSource),
                object: object,
              },
            ),
            changes: activeChanges,
          },
        ]
      }
      case VirtualCardRevisionEvent.Approved: {
        return [
          {
            label: translateNode('${subject} approved this ${cardType}', {
              subject: subject,
              cardType: translateNode(this.cardTypeSource),
            }),
            changes: activeChanges,
          },
        ]
      }
      case VirtualCardRevisionEvent.Rejected: {
        return [
          {
            label: translateNode('${subject} rejected this ${cardType}', {
              subject: subject,
              cardType: translateNode(this.cardTypeSource),
            }),
            changes: [],
          },
        ]
      }
      case VirtualCardRevisionEvent.Cancelled: {
        return [
          {
            label: translateNode('${subject} cancelled this ${cardType}', {
              subject: subject,
              cardType: translateNode(this.cardTypeSource),
            }),
            changes: [],
          },
        ]
      }
      case VirtualCardRevisionEvent.Expired: {
        return [
          {
            label: translateNode('${subject} expired', {
              subject: subject,
            }),
            changes: [],
          },
        ]
      }
      case VirtualCardRevisionEvent.ModApproved: {
        return [
          {
            label: translateNode('${subject} approved changes', {
              subject: subject,
            }),
            changes: activeChanges,
          },
        ]
      }
      case VirtualCardRevisionEvent.ModCancelled: {
        return [
          {
            label: translateNode('${subject} cancelled changes', {
              subject: subject,
            }),
            changes: [],
          },
        ]
      }
      case VirtualCardRevisionEvent.ModRejected: {
        return [
          {
            label: translateNode('${subject} rejected changes', {
              subject: subject,
            }),
            changes: [],
          },
        ]
      }
      case VirtualCardRevisionEvent.ModRequested: {
        const cds = []
        const fundingSourceOwnerApproval =
          this.after?.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval &&
          this.before?.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingCreditCardUserApproval
        if (Object.keys(pendingChanges).length) {
          if (fundingSourceOwnerApproval) {
            cds.push({
              label: translateNode('${subject} approved changes', {
                subject: subject,
              }),
              changes: [...pendingChanges, ...activeChanges],
            })
          } else {
            cds.push({
              label: translateNode('${subject} requested changes', {
                subject: subject,
              }),
              changes: pendingChanges,
            })
          }
        }
        if (Object.keys(activeChanges).length) {
          if (!fundingSourceOwnerApproval) {
            cds.push({
              label: translateNode('${subject} made changes', {
                subject: subject,
              }),
              changes: activeChanges,
            })
          }
        }
        return cds
      }
      case VirtualCardRevisionEvent.IssuerCycled:
      case VirtualCardRevisionEvent.Recurred: {
        const resetAmount =
          this.after?.recurrence !== undefined
            ? this.after.recurrence.balance
            : (this.after?.limit ?? this.after?.balance)

        return [
          {
            label: this.after?.awaitingBudget
              ? translateNode(
                  '${subject} failed to reset due to budget constraints',
                  {
                    subject: subject,
                  },
                )
              : resetAmount !== undefined
                ? translateNode('${subject} reset to ${amount}', {
                    subject: subject,
                    amount: <Crncy currency={currency} amount={resetAmount} />,
                  })
                : translateNode('${subject} reset', {
                    subject: subject,
                  }),
            changes: [],
          },
        ]
      }
      case VirtualCardRevisionEvent.Restored: {
        return [
          {
            label: translateNode('${subject} restored this ${cardType}', {
              subject: subject,
              cardType: translateNode(this.cardTypeSource),
            }),
            changes: activeChanges,
          },
        ]
      }
      case VirtualCardRevisionEvent.Updated: {
        if (
          this.before?.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval &&
          this.after?.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval
        ) {
          return [
            {
              label: translateNode('${subject} approved this ${cardType}', {
                subject: subject,
                cardType: translateNode(this.cardTypeSource),
              }),
              changes: activeChanges,
            },
          ]
        }
        if (
          this.before?.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingCreditCardUserApproval &&
          this.after?.awaitingApprovalStatus ===
            ApprovalStatus.AwaitingApproverRuleApproval
        ) {
          return [
            {
              label: translateNode('${subject} approved this ${cardType}', {
                subject: subject,
                cardType: translateNode(this.cardTypeSource),
              }),
              changes: activeChanges,
            },
          ]
        }
        return [
          {
            label: translateNode('${subject} made changes', {
              subject: subject,
            }),
            changes: activeChanges,
          },
        ]
      }
      case VirtualCardRevisionEvent.Closed: {
        return [
          {
            label: translateNode(
              '${subject} permanently closed this ${cardType}',
              {
                subject: subject,
                cardType: translateNode(this.cardTypeSource),
              },
            ),
            changes: activeChanges,
          },
        ]
      }
      default: {
        return []
      }
    }
  }

  private get cardTypeSource(): string {
    switch (this.cardType) {
      case VirtualCardType.BillPay:
        return 'bill pay card'
      case VirtualCardType.IssuerCycle:
      case VirtualCardType.Standard:
      case VirtualCardType.Recurring:
      case VirtualCardType.Direct:
      case VirtualCardType.RecurringGuest:
      case VirtualCardType.DecliningBalance:
      case undefined:
        return 'card'
    }
  }

  private get limitLabelSource(): string {
    switch (this.cardType) {
      case VirtualCardType.IssuerCycle:
        return 'Monthly Credit Limit'
      case VirtualCardType.BillPay:
        return 'Invoice Amount'
      case VirtualCardType.Standard:
      case VirtualCardType.Recurring:
      case VirtualCardType.Direct:
      case VirtualCardType.RecurringGuest:
      case VirtualCardType.DecliningBalance:
      case undefined:
        return 'Credit Limit'
    }
  }

  private get receiptLabelSource(): string {
    switch (this.cardType) {
      case VirtualCardType.BillPay:
        return 'Invoice'
      case VirtualCardType.IssuerCycle:
      case VirtualCardType.Standard:
      case VirtualCardType.Recurring:
      case VirtualCardType.Direct:
      case VirtualCardType.RecurringGuest:
      case VirtualCardType.DecliningBalance:
      case undefined:
        return 'Attachments'
    }
  }
}

interface RevisionDiffResponse {
  displayName?: string
  currency?: ExtendCurrency
  awaitingApprovalStatus?: ApprovalStatus
  balanceCents?: number
  limitCents?: number
  spentCents?: number
  awaitingBudget?: boolean
  validFrom?: string
  validTo?: string
  activeUntil?: string
  recurs?: boolean
  recurrence?: RecurrenceResponse
  receiptAttachments?: string[]
  pending?: VirtualCardPendingRevisionResponse
  pendingModification?: boolean
  cardholder?: UserListResponse
  recipient?: UserListResponse
  status?: VirtualCardStatus
  notes?: string
  receiptRulesExempt?: boolean
  billPayMeta?: VirtualCardBillPayMetaResponse
  billPayInvoiceNumber?: string
  billPayInvoiceDate?: string
  issuerCycleConfig?: IssuerCycleConfig
}

class RevisionDiffModel {
  public displayName?: string
  public awaitingApprovalStatus?: ApprovalStatus
  public currency?: ExtendCurrency
  public balanceCents?: number
  public limitCents?: number
  public spentCents?: number
  public awaitingBudget?: boolean
  public validFrom?: string
  public validTo?: string
  public activeUntil?: string
  public recurs?: boolean
  public recurrence?: RecurrenceModel
  public receiptAttachments?: string[]
  public pending?: VirtualCardPendingRevisionModel
  public pendingModification?: boolean
  public cardholder?: UserListModel
  public recipient?: UserListModel
  public status?: VirtualCardStatus
  public notes?: string
  public receiptRulesExempt?: boolean
  public billPayMeta?: VirtualCardBillPayMetaModel
  public billPayInvoiceNumber?: string
  public billPayInvoiceDate?: string
  public issuerCycleConfig?: IssuerCycleConfig

  public constructor(data: RevisionDiffResponse) {
    this.displayName = data.displayName
    this.currency = data.currency
    this.awaitingApprovalStatus = data.awaitingApprovalStatus
    this.balanceCents = data.balanceCents
    this.limitCents = data.limitCents
    this.spentCents = data.spentCents
    this.awaitingBudget = data.awaitingBudget
    this.validFrom = data.validFrom
    this.validTo = data.validTo
    this.activeUntil = data.activeUntil
    this.recurs = data.recurs
    this.recurrence =
      data.recurrence !== undefined
        ? new RecurrenceModel(data.recurrence)
        : undefined
    this.receiptAttachments = data.receiptAttachments
    this.pending =
      data.pending !== undefined
        ? new VirtualCardPendingRevisionModel(data.pending)
        : undefined
    this.pendingModification = data.pendingModification
    this.cardholder =
      data.cardholder !== undefined
        ? new UserListModel(data.cardholder)
        : undefined
    this.recipient =
      data.recipient !== undefined
        ? new UserListModel(data.recipient)
        : undefined
    this.status = data.status
    this.notes = data.notes
    this.receiptRulesExempt = !!data.receiptRulesExempt
    this.billPayMeta =
      data.billPayMeta !== undefined
        ? new VirtualCardBillPayMetaModel(data.billPayMeta)
        : undefined
    this.billPayInvoiceNumber = data.billPayInvoiceNumber
    this.billPayInvoiceDate = data.billPayInvoiceDate
    this.issuerCycleConfig = data.issuerCycleConfig
  }

  public get isActive(): boolean {
    return this.status === VirtualCardStatus.Active
  }

  public get isPending(): boolean {
    return this.status === VirtualCardStatus.Pending
  }

  public get isInactive(): boolean {
    return !this.isActive && !this.isPending
  }

  public get isConsumed(): boolean {
    // subset of inactive
    return this.status === VirtualCardStatus.Consumed
  }

  public get isClosed(): boolean {
    return this.status === VirtualCardStatus.Closed
  }

  public get psuedoStatusLabel(): React.ReactElement {
    return this.isPending
      ? this.awaitingApprovalStatus ===
        ApprovalStatus.AwaitingApproverRuleApproval
        ? translateNode('Pending Final Approval')
        : translateNode('Pending')
      : this.isActive
        ? translateNode('Active')
        : this.isConsumed || this.isClosed
          ? translateNode('Closed')
          : translateNode('Inactive')
  }

  public get balance(): number | undefined {
    return this.balanceCents !== undefined
      ? toDollars(this.balanceCents)
      : undefined
  }

  public get limit(): number | undefined {
    return this.limitCents !== undefined
      ? toDollars(this.limitCents)
      : undefined
  }

  public validToTimezonedMoment(timezone: string): Moment | undefined {
    return this.validTo !== undefined
      ? moment.tz(this.validTo, timezone).subtract(1, 'millisecond')
      : undefined
  }

  public validToTimezonedString(timezone: string): string | undefined {
    const mom = this.validToTimezonedMoment(timezone)
    return mom !== undefined ? mom.format('ll') : undefined
  }

  public activeUntilTimezonedMoment(timezone: string): Moment | undefined {
    return this.activeUntil !== undefined
      ? moment.tz(this.activeUntil, timezone).subtract(1, 'millisecond')
      : undefined
  }

  public activeUntilTimezonedString(timezone: string): string | undefined {
    const mom = this.activeUntilTimezonedMoment(timezone)
    return mom !== undefined ? mom.format('ll') : undefined
  }

  public validFromTimezonedMoment(timezone: string): Moment | undefined {
    return this.validFrom !== undefined
      ? moment.tz(this.validFrom, timezone)
      : undefined
  }

  public validFromTimezonedString(timezone: string): string | undefined {
    const mom = this.validFromTimezonedMoment(timezone)
    return mom !== undefined ? mom.format('ll') : undefined
  }

  public get invoiceDateMoment(): Moment | undefined {
    const invoiceDate = this.billPayInvoiceDate ?? this.billPayMeta?.invoiceDate
    return invoiceDate !== undefined ? moment(invoiceDate) : undefined
  }

  public get invoiceDateString(): string | undefined {
    return this.invoiceDateMoment?.format('ll')
  }

  public get invoiceNumber(): string | undefined {
    return this.billPayInvoiceNumber ?? this.billPayMeta?.invoiceNumber
  }

  public get vendorEmail(): string | undefined {
    return this.billPayMeta?.vendor?.email
  }

  public get vendorName(): string | undefined {
    return this.billPayMeta?.vendorName
  }
}
